import { Fragment } from 'react'
import Link from 'next/link'

import AdvancedImage from '@components/image/WithFallback'
import resolveCloudflareSrcSet from '@components/image/functions/resolveCloudflareSrcSet'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import CardWatchButton from '@components/lists/CardWatchButton'
import useT from '@hooks/useTranslation'
import cloudflarePhoto from '@utils/links/cloudflarePhoto'
import fullPhoto from '@utils/links/fullPhoto'
import mix from '@utils/styles/mix'
import pluralize from '@utils/grammar/pluralize'

import { buyCardData, listingCardData, offerCardData } from './offerCardData'

import styles from './OfferCard.module.css'

// card43m: 800 × 600 cover (4:3 jak pole karty); list i x0grid są 3:2.
const SRC_SET = { '300w': 'list', '568w': 'x0grid', '800w': 'card43m' }

// Pole 4:3 z sylwetką pod zdjęciem. Brak zdjęcia: sylwetka i napis; błąd ładowania:
// obraz znika (WithFallback hideOnError) i zostaje sylwetka w tej samej geometrii.
function CardMedia ({ card, children, priority, sizes }) {
    const noPhoto = useT('Brak zdjęć')
    const { alt, photo, silhouette } = card

    return (
        <div className={styles.media}>
            { silhouette && (
                <svg aria-hidden="true" className={styles.ph} focusable="false" viewBox="0 0 120 48">
                    <use href={`#s-${silhouette}`} />
                </svg>
            ) }
            { photo
                ? <AdvancedImage
                    alt={alt}
                    className={styles.photo}
                    decoding="async"
                    height="378"
                    hideOnError
                    priority={priority}
                    sizes={photo.cloudflareId ? sizes : undefined}
                    src={photo.cloudflareId ? cloudflarePhoto(photo.cloudflareId, 'x0grid') : fullPhoto(photo.pid, 0, '1x')}
                    srcSet={photo.cloudflareId ? resolveCloudflareSrcSet(SRC_SET, photo.cloudflareId) : undefined}
                    width="568"
                />
                : <span className={styles.empty}>{noPhoto}</span> }
            { children }
        </div>
    )
}

function Badges ({ isNew, promo, small }) {
    const t = { promo: useT('Promowane'), isNew: useT('Nowy') }
    if (!promo && !isNew) return null
    return (
        <div className={styles.badges}>
            { promo && <span className={mix([styles.badge, styles.badgePromo, small && styles.badgeSm])}>{t.promo}</span> }
            { isNew && <span className={mix([styles.badge, styles.badgeSoft, small && styles.badgeSm])}>{t.isNew}</span> }
        </div>
    )
}

// Karta ogłoszenia: standardowa, `big` (duże okno wyróżnień), `row` (pozioma poniżej 640 px;
// rodzic ustawia container-type, żeby szerokość zdjęcia liczyła się od kontenera).
// priority: pierwszy widoczny obraz kart na stronie (eager, fetchpriority="high"), tylko jeden.
function OfferCard ({ as: Heading = 'h3', big = false, offer, priority = false, promo = false, row = false, sizes }) {
    const t = { photos: [useT('zdjęcie'), useT('zdjęcia'), useT('zdjęć')] }
    if (!offer) return null
    const card = offerCardData(offer)

    return (
        <article className={mix([styles.card, big && styles.big, row && styles.row, promo && !big && styles.promo])}>
            <CardMedia card={card} priority={priority} sizes={sizes}>
                { card.photo?.count > 0 && (
                    <span className={styles.count}>
                        <SvgIcon name="camera" size="sm" />{card.photo.count}<span className={styles.srOnly}> {pluralize(card.photo.count, t.photos)}</span>
                    </span>
                ) }
                <Badges isNew={card.isNew} promo={big || promo} small={row} />
                <CardWatchButton className={row ? styles.favRow : undefined} id={offer._id} title={card.title} type={offer.type} />
            </CardMedia>
            <div className={styles.body}>
                <Heading className={styles.title}>
                    <Link href={card.link} prefetch={false}>{card.title}</Link>
                </Heading>
                { card.version && <p className={styles.version}>{card.version}</p> }
                <Spec spec={card.spec} wide={card.specWide} />
                <Price price={card.price} />
                <Meta location={card.location} seller={card.seller} />
            </div>
        </article>
    )
}

// Karta kompaktowa (małe okno wyróżnień): zdjęcie, tytuł, dwa parametry, cena.
function CompactOfferCard ({ as: Heading = 'h3', offer, priority = false, role, sizes }) {
    if (!offer) return null
    const card = offerCardData(offer)
    const short = card.spec.slice(0, 2).map(item => item.short || item.value).join(', ')

    return (
        <article className={styles.ccard} role={role}>
            <CardMedia card={card} priority={priority} sizes={sizes}>
                <Badges isNew={false} promo small />
                <CardWatchButton id={offer._id} size="sm" title={card.title} type={offer.type} />
            </CardMedia>
            <div className={styles.cbody}>
                <Heading className={styles.ctitle}>
                    <Link href={card.link} prefetch={false}>{card.title}</Link>
                </Heading>
                { short && <p className={styles.cspec}>{short}</p> }
                <p className={styles.cprice}><PriceValue price={card.price} /></p>
            </div>
        </article>
    )
}

const BADGE_CLASS = { muted: styles.badgeMuted, promo: styles.badgePromo, soft: styles.badgeSoft, success: styles.badgeSuccess }

// Kwota z jednostką; bez kwoty — „Cena do uzgodnienia”.
function PriceValue ({ price }) {
    const ask = useT('Cena do uzgodnienia')
    return price.value ? <>{price.value} <span>{price.unit}</span></> : <span className={styles.priceAsk}>{ask}</span>
}

function Price ({ price }) {
    return (
        <div className={styles.priceRow}>
            <p className={styles.price}><PriceValue price={price} /></p>
            { price.note && <span className={styles.priceNote}>{price.note}</span> }
        </div>
    )
}

// Ikony lokalizacji i dealera jako maski CSS (::before).
function Meta ({ location, seller }) {
    const t = { dealer: useT('Firma'), private: useT('Prywatny') }
    if (!location && !seller) return null
    return (
        <div className={styles.meta}>
            { location ? <span className={styles.loc}>{location}</span> : <span /> }
            { seller === 'dealer' && <span className={mix([styles.seller, styles.dealer])}>{t.dealer}</span> }
            { seller === 'private' && <span className={styles.seller}>{t.private}</span> }
        </div>
    )
}

// Parametry: pary dt/dd bez opakowań (dt ukryte wizualnie); skrót widoczny, pełna nazwa dla czytników.
function Spec ({ spec, wide }) {
    if (!spec.length) return null
    return (
        <dl className={mix([styles.spec, wide && styles.specWide])}>
            { spec.map((item, i) => (
                <Fragment key={i}>
                    <dt>{item.label}</dt>
                    <dd>{item.short ? <><span aria-hidden="true">{item.short}</span><span className={styles.srOnly}>{item.value}</span></> : item.value}</dd>
                </Fragment>
            )) }
        </dl>
    )
}

// Karta listy ogłoszeń: jak OfferCard, z odznakami listy, wariantem części i notką raty.
// Widok listy (od 1024 px) ustawia klasa listView na kontenerze siatki.
// host: adres serwisu przed linkiem (strony na innej domenie, np. wizytówka na subdomenie).
function ListingOfferCard ({ host = '', isFresh = false, offer, priority = false, sizes }) {
    if (!offer) return null
    const card = listingCardData(offer, { isFresh })
    const highlighted = card.badges.some(b => b.kind === 'promo')

    return (
        <article className={mix([styles.card, styles.listing, card.part && styles.part, highlighted && styles.promo, card.gallery && styles.withGal])}>
            <CardMedia card={card} priority={priority} sizes={sizes}>
                { card.gallery && <Gallery gallery={card.gallery} title={card.title} /> }
                { card.badges.length > 0 && (
                    <div className={styles.badges}>
                        { card.badges.map(b => <span className={mix([styles.badge, BADGE_CLASS[b.kind]])} key={b.kind}>{b.label}</span>) }
                    </div>
                ) }
                <CardWatchButton id={offer._id} title={card.title} type={offer.type} />
            </CardMedia>
            <div className={styles.body}>
                <h3 className={styles.title}>
                    <Link href={host + card.link} prefetch={false}>{card.title}</Link>
                </h3>
                { card.part ? <>
                    { card.part.category && <p className={styles.partCat}>{card.part.category}</p> }
                    { card.part.delivery.length > 0 && (
                        <div className={styles.partTags}>
                            { card.part.delivery.map((d, i) => <span className={mix([styles.badge, i ? styles.badgeMuted : styles.badgeSoft])} key={d}>{d}</span>) }
                        </div>
                    ) }
                </> : <>
                    { card.version && <p className={styles.version}>{card.version}</p> }
                    <Spec spec={card.spec} wide={card.specWide} />
                </> }
                <Price price={card.price} />
                <Meta location={card.location} seller={card.seller} />
            </div>
        </article>
    )
}

// Kolejne zdjęcia w karcie: tor przewijany w poziomie nad zdjęciem karty (pierwszy slajd przezroczysty),
// kropki i strzałki. Slajdy poza Tab i drzewem dostępności; obrazy, przewijanie przyciskami i przejście
// do ogłoszenia obsługuje jeden kontroler listy (GalleryController), obrazy dopiero przy pierwszej interakcji.
function Gallery ({ gallery, title }) {
    const t = { prev: useT('Poprzednie zdjęcie'), next: useT('Następne zdjęcie'), photos: [useT('zdjęcie'), useT('zdjęcia'), useT('zdjęć')], inOffer: useT('w ogłoszeniu') }
    const { ids, more } = gallery
    const n = ids.length + (more > 0 ? 1 : 0)

    return <>
        <div aria-hidden="true" className={styles.gal} data-gal={ids.slice(1).join(',')} tabIndex={-1}>
            { ids.map(id => <span className={styles.galSlide} key={id} />) }
            { more > 0 && <span className={mix([styles.galSlide, styles.galMore])}>+{more}<small>{pluralize(more, t.photos)} {t.inOffer}</small></span> }
        </div>
        <span aria-hidden="true" className={styles.galDots} style={{ '--n': n }} />
        <button aria-label={`${t.prev}: ${title}`} className={mix([styles.galBtn, styles.galPrev])} data-gal-step="-1" disabled type="button" />
        <button aria-label={`${t.next}: ${title}`} className={mix([styles.galBtn, styles.galNext])} data-gal-step="1" type="button" />
    </>
}

// Karta „kupię”: bez zdjęcia i bez obserwowania (ogłoszeń kupna nie ma w obserwowanych).
function BuyOfferCard ({ offer }) {
    const buy = useT('Kupię')
    if (!offer) return null
    const card = buyCardData(offer)

    return (
        <article className={mix([styles.card, styles.listing, styles.buy])}>
            <div className={styles.body}>
                <h3 className={styles.title}>
                    <Link href={card.link} prefetch={false}><span className={styles.buyPre}>{buy}</span> {card.title}</Link>
                </h3>
                { card.spec.length > 0 && <p className={styles.version}>{card.spec.join(', ')}</p> }
                <Price price={card.price} />
                <Meta location={card.location} />
            </div>
        </article>
    )
}

export {
    BuyOfferCard,
    CompactOfferCard,
    ListingOfferCard,
    OfferCard,
}
